import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth-store';
import { RealtimeChannel } from '@/lib/realtime';
import {
  notificationsCache,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsQuery,
  useUnreadCountQuery,
} from '@/hooks/queries/useNotifications';
import { cn } from '@/lib/utils';

/**
 * The bell-icon widget in the header. Owns the app-shell-level WebSocket
 * subscription to `/ws/notifications` — a single connection per signed-in
 * tab that pushes new notifications into TanStack Query cache.
 */
export function NotificationBell() {
  const { state } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isAuthed = state.status === 'authenticated';
  const token = isAuthed ? state.token : '';

  const { data: unread } = useUnreadCountQuery(token, isAuthed);
  const { data: list } = useNotificationsQuery(token, isAuthed && open);
  const markRead = useMarkNotificationReadMutation(token);
  const markAll = useMarkAllNotificationsReadMutation(token);

  // Single persistent WS to /ws/notifications while authenticated.
  useEffect(() => {
    if (!isAuthed) return;
    const channel = new RealtimeChannel('/ws/notifications', () => token);
    const unsub = channel.subscribe((event) => {
      if (
        event &&
        typeof event === 'object' &&
        (event as { event?: unknown }).event === 'notification'
      ) {
        const n = (event as { notification: import('@/lib/api').NotificationShape }).notification;
        notificationsCache.prepend(queryClient, { ...n, payload: null, readAt: null });
      }
    });
    void channel.connect();
    return () => {
      unsub();
      channel.close();
    };
  }, [isAuthed, token, queryClient]);

  // Outside-click to close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!isAuthed) return null;

  const unreadCount = unread?.unread ?? 0;
  const items = list?.notifications ?? [];

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications (${unreadCount} unread)`}
        className="relative rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 overflow-hidden rounded-xl border border-border bg-card shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-sm font-semibold">Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAll.mutateAsync()}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          <ul className="max-h-96 divide-y divide-border overflow-y-auto">
            {items.length === 0 ? (
              <li className="p-4 text-sm text-muted-foreground">You're all caught up.</li>
            ) : (
              items.map((n) => (
                <li key={n.id} className={cn('p-3 text-sm', !n.readAt && 'bg-secondary/30')}>
                  <div className="flex items-start gap-2">
                    {n.targetUrl ? (
                      <Link
                        to={n.targetUrl}
                        onClick={() => {
                          setOpen(false);
                          if (!n.readAt) void markRead.mutateAsync(n.id);
                        }}
                        className="flex-1 hover:underline"
                      >
                        <div className={cn('font-medium', !n.readAt && 'font-semibold')}>
                          {n.title}
                        </div>
                        {n.body && <div className="mt-0.5 text-muted-foreground">{n.body}</div>}
                      </Link>
                    ) : (
                      <div className="flex-1">
                        <div className={cn('font-medium', !n.readAt && 'font-semibold')}>
                          {n.title}
                        </div>
                        {n.body && <div className="mt-0.5 text-muted-foreground">{n.body}</div>}
                      </div>
                    )}
                    {!n.readAt && (
                      <button
                        type="button"
                        aria-label="Mark read"
                        onClick={() => void markRead.mutateAsync(n.id)}
                        className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {new Date(n.createdAt).toLocaleString()}
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
